import {
  DayPartSections,
  GroupByMode,
  ID,
  overdueSectionID,
  ReorderSectionUpdate,
  SortingMode,
  TaskPriorityOptions,
  TypeSections,
  UserSettings,
} from '@griply/types';

export type BuildTodayReorderUserSettingsOptions = {
  updatedSections: ReorderSectionUpdate[];
  groupBy: GroupByMode | undefined | null;
  // Mobile passes true: the legacy keys this reorder touches are nulled, signalling
  // the converter that the new store owns them. Web dual-writes the legacy order.
  clearLegacy?: boolean;
  // None has no date-keyed legacy of its own; the caller supplies which shared
  // `todoSorting` keys to null when migrating it.
  legacyTodoSortingKeysToClear?: ID[];
};

// Values are `null` at runtime (Firestore clears the key); typed as ID[] to fit the
// legacy shapes.
const nullifyKeys = (keys: ID[]): Record<ID, ID[]> =>
  keys.reduce<Record<ID, ID[]>>((acc, key) => {
    acc[key] = null as unknown as ID[];
    return acc;
  }, {});

export const buildTodayReorderUserSettings = ({
  updatedSections,
  groupBy,
  clearLegacy,
  legacyTodoSortingKeysToClear,
}: BuildTodayReorderUserSettingsOptions): Partial<UserSettings> | null => {
  const currentGroupMode = groupBy ?? GroupByMode.Deadline;

  // Overdue is a virtual section; never persisted.
  const newOrder = updatedSections.reduce<Record<ID, ID[]>>((acc, section) => {
    if (section.id === overdueSectionID) {
      return acc;
    }
    acc[section.id] = section.taskSorting;
    return acc;
  }, {});

  if (!Object.keys(newOrder).length) {
    return null;
  }

  const isNone = currentGroupMode === GroupByMode.None;
  const newSlotValue = isNone ? Object.values(newOrder).flat() : newOrder;
  const touchedKeys = Object.keys(newOrder);

  const legacySettings: Partial<UserSettings> = {
    todoSortingMode: SortingMode.Custom,
  };

  if (isNone) {
    // The flat None order can't be written back into the date-keyed `todoSorting`
    // losslessly, so we null the today buckets it spans and let the new store win.
    const keys = legacyTodoSortingKeysToClear ?? [];
    if (keys.length) {
      legacySettings.todoSorting = nullifyKeys(keys);
    }
  }
  if (groupBy === GroupByMode.Deadline) {
    legacySettings.todoSorting = clearLegacy
      ? nullifyKeys(touchedKeys)
      : newOrder;
  }
  if (groupBy === GroupByMode.Goal) {
    legacySettings.todayGroupSorting = {
      goal: { sorting: clearLegacy ? nullifyKeys(touchedKeys) : newOrder },
    };
  }
  if (groupBy === GroupByMode.LifeArea) {
    legacySettings.todayGroupSorting = {
      lifeArea: { sorting: clearLegacy ? nullifyKeys(touchedKeys) : newOrder },
    };
  }
  if (groupBy === GroupByMode.Priority) {
    legacySettings.todayGroupSorting = {
      priority: {
        sorting: (clearLegacy ? nullifyKeys(touchedKeys) : newOrder) as Partial<
          Record<TaskPriorityOptions, ID[]>
        >,
      },
    };
  }
  if (groupBy === GroupByMode.Type) {
    legacySettings.todayGroupSorting = {
      type: {
        sorting: (clearLegacy ? nullifyKeys(touchedKeys) : newOrder) as Partial<
          Record<TypeSections, ID[]>
        >,
      },
    };
  }
  if (groupBy === GroupByMode.DayPart) {
    legacySettings.todayGroupSorting = {
      dayPart: {
        sorting: (clearLegacy ? nullifyKeys(touchedKeys) : newOrder) as Partial<
          Record<DayPartSections, ID[]>
        >,
      },
    };
  }

  return {
    customTaskOrder: { today: { [currentGroupMode]: newSlotValue } },
    filters: { today: { sortingMode: SortingMode.Custom } },
    ...legacySettings,
  };
};
